import React, { useEffect, useState, useMemo } from "react";
import { PortalService } from "services/portal";
import { ChevronRight, Folder, FileText, ArrowLeft, ExternalLink, Download } from "lucide-react";
import { DriveErrorState } from "components/DriveErrorState";
import config from "services/config";

const CARD = { background: "var(--bg-card)", borderRadius: 12, border: "1px solid var(--border)", overflow: "hidden" };

const CATEGORY_COLOR = {
  main: { bg: "#eff6ff", color: "#3b82f6" },
  worker: { bg: "#ecfdf5", color: "#10b981" },
  "worker-year": { bg: "#ecfdf5", color: "var(--positive)" },
  "cotizacion-year": { bg: "#fef3c7", color: "var(--warning)" },
  custom: { bg: "#f3e8ff", color: "#9333ea" },
  sub: { bg: "#f1f5f9", color: "var(--text-secondary)" },
};

function colorFor(category) {
  return CATEGORY_COLOR[category] || CATEGORY_COLOR.custom;
}

export function PortalDocumentos() {
  const [tree, setTree] = useState([]);
  const [loadingTree, setLoadingTree] = useState(true);
  const [treeError, setTreeError] = useState(null);

  // Path: array de nodos del árbol que llevan al folder actual.
  const [path, setPath] = useState([]);
  const [files, setFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [filesError, setFilesError] = useState(null);

  const loadTree = () => {
    setLoadingTree(true);
    setTreeError(null);
    PortalService.getDocuments()
      .then((res) => setTree(res.tree || []))
      .catch((err) => setTreeError(err?.response?.data?.error || err.message))
      .finally(() => setLoadingTree(false));
  };

  useEffect(() => { loadTree(); }, []);

  // Items navegables del nivel actual (children del último nodo del path; si no hay path, el árbol root).
  const currentNodes = useMemo(() => {
    if (path.length === 0) return tree;
    return path[path.length - 1].children || [];
  }, [path, tree]);

  const currentFolderId = path.length > 0 ? path[path.length - 1].id : null;

  // Cargar archivos cuando entramos a una carpeta con id.
  useEffect(() => {
    if (!currentFolderId) { setFiles([]); return; }
    setLoadingFiles(true);
    setFilesError(null);
    PortalService.getDocumentFolderFiles(currentFolderId)
      .then((data) => {
        const onlyFiles = (data || []).filter(
          (f) => f.mimeType !== "application/vnd.google-apps.folder"
        );
        setFiles(onlyFiles);
      })
      .catch((err) => {
        setFiles([]);
        setFilesError(err?.response?.data?.error || err.message);
      })
      .finally(() => setLoadingFiles(false));
  }, [currentFolderId]);

  const navigateInto = (node) => {
    if (!node) return;
    setPath([...path, node]);
  };

  const navigateTo = (index) => {
    setPath(path.slice(0, index + 1));
  };

  const navigateRoot = () => setPath([]);

  if (loadingTree) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "60vh" }}>
        <p style={{ color: "var(--text-secondary)" }}>Cargando documentos...</p>
      </div>
    );
  }

  if (treeError) {
    return <DriveErrorState message={treeError} onRetry={loadTree} />;
  }

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: "var(--text-primary)", marginBottom: 4 }}>Documentos</h1>
        <p style={{ fontSize: 14, color: "var(--text-secondary)" }}>Tus carpetas y archivos en Google Drive</p>
      </div>

      {/* Breadcrumb */}
      <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 4, marginBottom: 16, fontSize: 13, color: "var(--text-secondary)" }}>
        {path.length > 0 && (
          <button
            onClick={() => setPath(path.slice(0, -1))}
            style={{ background: "transparent", border: "none", cursor: "pointer", color: "var(--text-secondary)", display: "flex", alignItems: "center", gap: 4, padding: "4px 8px", borderRadius: 6 }}
          >
            <ArrowLeft size={14} /> Atrás
          </button>
        )}
        <button onClick={navigateRoot} style={{ background: "transparent", border: "none", cursor: "pointer", color: path.length === 0 ? "#0f172a" : "#0353a4", fontWeight: path.length === 0 ? 600 : 400 }}>
          Inicio
        </button>
        {path.map((p, i) => (
          <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            <ChevronRight size={14} color="#cbd5e1" />
            <button
              onClick={() => navigateTo(i)}
              style={{ background: "transparent", border: "none", cursor: "pointer", color: i === path.length - 1 ? "#0f172a" : "#0353a4", fontWeight: i === path.length - 1 ? 600 : 400 }}
            >
              {p.name}
            </button>
          </span>
        ))}
      </div>

      {/* Folders del nivel actual */}
      {currentNodes.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 16 }}>
          {currentNodes.map((node, i) => {
            const c = colorFor(node.category);
            return (
              <div
                key={node.id || `n-${i}`}
                onClick={() => navigateInto(node)}
                style={{
                  ...CARD,
                  padding: 16,
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  cursor: "pointer",
                  transition: "all 0.15s",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 4px 10px rgba(0,0,0,0.06)"; e.currentTarget.style.borderColor = "#cbd5e1"; }}
                onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.borderColor = "#e8ecf1"; }}
              >
                <div style={{ width: 40, height: 40, borderRadius: 10, background: c.bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <Folder size={20} color={c.color} />
                </div>
                <div style={{ overflow: "hidden" }}>
                  <p style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)", marginBottom: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {node.name}
                  </p>
                  <p style={{ fontSize: 11, color: "var(--text-muted)" }}>
                    {(node.children?.length || 0) > 0 ? `${node.children.length} carpetas` : "Carpeta"}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Archivos */}
      {currentFolderId && (
        <div style={{ ...CARD, padding: 16 }}>
          <h3 style={{ fontSize: 13, fontWeight: 600, color: "var(--text-secondary)", marginBottom: 12 }}>Archivos</h3>
          {loadingFiles ? (
            <p style={{ color: "var(--text-muted)", fontSize: 13, padding: "16px 0" }}>Cargando archivos...</p>
          ) : filesError ? (
            <DriveErrorState
              message={filesError}
              onRetry={() => {
                setLoadingFiles(true);
                setFilesError(null);
                PortalService.getDocumentFolderFiles(currentFolderId)
                  .then((data) => setFiles((data || []).filter((f) => f.mimeType !== "application/vnd.google-apps.folder")))
                  .catch((err) => setFilesError(err?.response?.data?.error || err.message))
                  .finally(() => setLoadingFiles(false));
              }}
              compact
            />
          ) : files.length === 0 ? (
            <p style={{ color: "var(--text-muted)", fontSize: 13, padding: "12px 0" }}>No hay archivos en esta carpeta</p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {files.map((f) => (
                <div
                  key={f.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "8px 10px",
                    borderRadius: 6,
                    cursor: "pointer",
                    transition: "background 0.1s",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "var(--bg-elevated)")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                >
                  <FileText size={18} color="#64748b" />
                  <span style={{ flex: 1, fontSize: 13, color: "var(--text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {f.name}
                  </span>
                  {f.webViewLink && (
                    <a
                      href={f.webViewLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Abrir en Drive"
                      style={{ color: "var(--accent)", display: "flex", alignItems: "center", padding: 4 }}
                    >
                      <ExternalLink size={16} />
                    </a>
                  )}
                  <a
                    href={`${config.url}/gdrive/file/${f.id}/download`}
                    title="Descargar"
                    style={{ color: "var(--text-secondary)", display: "flex", alignItems: "center", padding: 4 }}
                  >
                    <Download size={16} />
                  </a>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Empty state cuando no hay nada */}
      {!currentFolderId && currentNodes.length === 0 && (
        <div style={{ ...CARD, padding: 40, textAlign: "center" }}>
          <Folder size={48} color="#cbd5e1" style={{ margin: "0 auto 12px" }} />
          <p style={{ color: "var(--text-muted)", fontSize: 14 }}>No hay carpetas configuradas todavía</p>
        </div>
      )}
    </div>
  );
}
